import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  StarIcon,
  HeartIcon,
  UserGroupIcon,
  ClockIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const Discover = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    has_next: false,
    has_prev: false
  });
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    category: '',
    tech_stack: '',
    team_name: '',
    looking_for: '',
    image_url: '',
  });

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'web_software_development', label: 'Web & Software Development' },
    { value: 'mobile_apps', label: 'Mobile Apps' },
    { value: 'ai_machine_learning', label: 'AI & Machine Learning' },
    { value: 'blockchain_crypto', label: 'Blockchain & Crypto' },
    { value: 'game_development', label: 'Game Development' },
    { value: 'ar_vr_xr', label: 'AR/VR/XR' },
    { value: 'hardware_iot', label: 'Hardware & IoT' },
    { value: 'cybersecurity', label: 'Cybersecurity' },
    { value: 'devops_infrastructure', label: 'DevOps & Infrastructure' },
    { value: 'visual_arts', label: 'Visual Arts' },
    { value: 'music_audio_projects', label: 'Music & Audio' },
    { value: 'film_video_animation', label: 'Film, Video & Animation' },
    { value: 'graphic_design_branding', label: 'Graphic Design & Branding' },
    { value: 'photography', label: 'Photography' },
    { value: 'fashion_apparel', label: 'Fashion & Apparel' },
    { value: 'performing_arts', label: 'Performing Arts' },
    { value: 'creative_writing_literature_comics', label: 'Writing & Literature' },
    { value: 'startup_mvp_development', label: 'Startup & MVP Development' },
    { value: 'ecommerce_online_stores', label: 'E-commerce & Online Stores' },
    { value: 'saas_b2b_solutions', label: 'SaaS & B2B Solutions' },
    { value: 'finance_fintech', label: 'Finance & Fintech' },
    { value: 'real_estate_proptech', label: 'Real Estate & PropTech' },
    { value: 'hr_recruitment_platforms', label: 'HR & Recruitment' },
    { value: 'marketing_advertising_projects', label: 'Marketing & Advertising' },
    { value: 'legaltech', label: 'LegalTech' },
    { value: 'nonprofits_ngos', label: 'Nonprofits & NGOs' },
    { value: 'education_learning_platforms', label: 'Education & Learning' },
    { value: 'mental_health_wellbeing', label: 'Mental Health & Wellbeing' },
    { value: 'environmental_sustainability_projects', label: 'Environmental & Sustainability' },
    { value: 'diversity_inclusion_initiatives', label: 'Diversity & Inclusion' },
    { value: 'local_community_building', label: 'Local Community Building' },
    { value: 'healthtech_medtech', label: 'HealthTech & MedTech' },
    { value: 'biotechnology_biohacking', label: 'Biotechnology & Biohacking' },
    { value: 'scientific_research_collaborations', label: 'Scientific Research' },
    { value: 'space_aerospace_projects', label: 'Space & Aerospace' },
    { value: 'university_student_projects', label: 'University & Student Projects' },
    { value: 'hackathon_ideas', label: 'Hackathon Ideas' },
    { value: 'open_source_contributions', label: 'Open Source Contributions' },
    { value: 'experimental_conceptual_projects', label: 'Experimental & Conceptual' },
    { value: 'hobby_projects', label: 'Hobby Projects' },
    { value: 'diy_maker_hardware', label: 'DIY & Maker Hardware' },
    { value: 'events_festivals', label: 'Events & Festivals' },
    { value: 'crowdfunding_campaigns', label: 'Crowdfunding Campaigns' },
    { value: 'lifestyle_productivity_tools', label: 'Lifestyle & Productivity Tools' }
  ];

  const navigate = useNavigate();

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: 1,
        limit: 12,
        sort_by: sortBy,
      };
      
      // Only add category if it has a value and is not 'all'
      if (selectedCategory && selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      
      // Only add search if it has a value
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await api.projects.getAll(params);
      
      // Handle the response format with pagination
      if (response.projects) {
        setProjects(response.projects);
        setPagination({
          total: response.total || 0,
          has_next: response.has_next || false,
          has_prev: response.has_prev || false
        });
      } else {
        setProjects(response || []);
        setPagination({
          total: 0,
          has_next: false,
          has_prev: false
        });
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects on component mount and when filters change
  useEffect(() => {
    fetchProjects();
  }, [selectedCategory, sortBy]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProjects();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle like project
  const handleLike = async (projectId) => {
    try {
      await api.projects.like(projectId);
      // Refresh projects to get updated like count
      fetchProjects();
    } catch (err) {
      console.error('Error liking project:', err);
    }
  };

  // Handle star project
  const handleStar = async (projectId) => {
    try {
      await api.projects.star(projectId);
      // Refresh projects to get updated star count
      fetchProjects();
    } catch (err) {
      console.error('Error starring project:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category.toLowerCase());
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      setCreatingProject(true);
      
      // Convert tech_stack string to array and prepare project data
      const projectData = {
        ...newProject,
        tech_stack: newProject.tech_stack.split(',').map(tech => tech.trim()).filter(tech => tech),
        // Ensure all required fields are present
        title: newProject.title.trim(),
        description: newProject.description.trim(),
        category: newProject.category,
        team_name: newProject.team_name.trim(),
        looking_for: newProject.looking_for.trim(),
        image_url: newProject.image_url.trim() || undefined, // Make optional if empty
      };
      
      await api.projects.create(projectData);
      
      // Reset form and close modal
    setNewProject({
      title: '',
      description: '',
      category: '',
        tech_stack: '',
        team_name: '',
        looking_for: '',
        image_url: '',
    });
      setShowCreateModal(false);
      
      // Refresh projects list
      fetchProjects();
    } catch (err) {
      console.error('Error creating project:', err);
      alert('Failed to create project. Please try again.');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProject(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading && projects.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Discover Projects
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Find innovative projects and join teams that match your interests and skills.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Project
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              {loading && (
                <div className="absolute right-3 top-2.5">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center space-x-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Options */}
          <div className="flex items-center space-x-2">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="created_at">Newest</option>
              <option value="trending">Trending</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchProjects}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            {searchQuery || selectedCategory !== 'all' 
              ? 'No projects found matching your criteria.' 
              : 'No projects available at the moment.'
            }
          </p>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white/10 backdrop-blur-md rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300 flex flex-col"
            onClick={() => navigate(`/projects/${project.id}`)}
            style={{ cursor: 'pointer' }}
          >
            {/* Project Image */}
            {project.image_url ? (
              <img
                src={project.image_url}
                alt={project.title}
                className="w-full h-48 object-cover"
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            
            {/* Project Image Placeholder with random gradient and modern abstract SVG */}
            {(!project.image_url) && (
              (() => {
                // Generate a deterministic color pair based on project id or title
                const gradients = [
                  ['from-pink-200', 'to-yellow-200', 'dark:from-pink-900', 'dark:to-yellow-900'],
                  ['from-indigo-100', 'to-purple-100', 'dark:from-indigo-900', 'dark:to-purple-900'],
                  ['from-green-100', 'to-blue-100', 'dark:from-green-900', 'dark:to-blue-900'],
                  ['from-yellow-100', 'to-pink-100', 'dark:from-yellow-900', 'dark:to-pink-900'],
                  ['from-blue-100', 'to-cyan-100', 'dark:from-blue-900', 'dark:to-cyan-900'],
                  ['from-purple-100', 'to-fuchsia-100', 'dark:from-purple-900', 'dark:to-fuchsia-900'],
                ];
                // Use project.id or title to pick a gradient
                const hash = (project.id || project.title || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                const grad = gradients[hash % gradients.length];
                return (
                  <div className={`w-full h-48 flex items-center justify-center bg-gradient-to-br ${grad[0]} ${grad[1]} ${grad[2]} ${grad[3]}`}>
                    <svg
                      width="120"
                      height="120"
                      viewBox="0 0 120 120"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="opacity-90"
                      aria-label="Project placeholder"
                    >
                      {/* Background circles */}
                      <circle cx="40" cy="40" r="30" fill="#c7d2fe" opacity="0.4"/>
                      <circle cx="85" cy="80" r="20" fill="#a5b4fc" opacity="0.3"/>
                      {/* Floating window */}
                      <rect x="30" y="35" rx="10" width="60" height="40" fill="#6366f1" />
                      {/* Window header */}
                      <rect x="30" y="35" rx="10" width="60" height="10" fill="#818cf8" />
                      {/* Window content lines */}
                      <rect x="40" y="50" width="40" height="4" rx="2" fill="#fff" opacity="0.7"/>
                      <rect x="40" y="57" width="30" height="4" rx="2" fill="#fff" opacity="0.5"/>
                      {/* Geometric shapes */}
                      <rect x="80" y="30" width="8" height="8" rx="2" fill="#fbbf24" transform="rotate(15 80 30)" />
                      <circle cx="35" cy="80" r="6" fill="#f472b6" />
                      <rect x="60" y="90" width="12" height="4" rx="2" fill="#34d399" />
                      {/* Decorative dots */}
                      <circle cx="25" cy="25" r="1.5" fill="#fff" />
                      <circle cx="100" cy="40" r="1.5" fill="#fff" />
                      <circle cx="90" cy="100" r="1" fill="#fff" />
                      <circle cx="30" cy="100" r="1.2" fill="#fff" />
                    </svg>
                  </div>
                );
              })()
            )}
            
            <div className="p-6 flex-grow flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                  {categories.find(cat => cat.value === project.category)?.label || project.category}
                </span>
                {project.created_at && (
                  <div className="flex items-center text-gray-500 dark:text-gray-400">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {project.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm line-clamp-3">
                {project.description}
              </p>
              
              {/* Team Info */}
              {project.team_name && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Team: {project.team_name}</p>
                  {project.looking_for && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Looking for: {project.looking_for}
                    </p>
                  )}
                </div>
              )}

              {/* Tech Stack */}
              {project.tech_stack && project.tech_stack.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {project.tech_stack.slice(0, 3).map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.tech_stack.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                        +{project.tech_stack.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-auto flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={e => { e.stopPropagation(); handleLike(project.id); }}
                    className="flex items-center hover:text-red-500 transition-colors"
                  >
                    <HeartIcon className="h-5 w-5 text-red-500 mr-1" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {project.likes_count || 0}
                    </span>
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); handleStar(project.id); }}
                    className="flex items-center hover:text-yellow-500 transition-colors"
                  >
                    <StarIcon className="h-5 w-5 text-yellow-500 mr-1" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {project.stars_count || 0}
                    </span>
                  </button>
                </div>

                {/* Creator Info */}
                {project.creator_name && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Created by {project.creator_name}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Project</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Project Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={newProject.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={newProject.description}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  name="category"
                  value={newProject.category}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select Category</option>
                  {categories.slice(1).map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tech Stack (comma separated)
                </label>
                <input
                  type="text"
                  name="tech_stack"
                  value={newProject.tech_stack}
                  onChange={handleInputChange}
                  placeholder="React, Node.js, MongoDB"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Team Name
                </label>
                <input
                  type="text"
                  name="team_name"
                  value={newProject.team_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Looking For
                </label>
                <input
                  type="text"
                  name="looking_for"
                  value={newProject.looking_for}
                  onChange={handleInputChange}
                  placeholder="Frontend Developer, Designer"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  name="image_url"
                  value={newProject.image_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Provide a URL to an image that represents your project
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creatingProject}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingProject}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {creatingProject ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discover;